/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MenuItem, ProductListItem, PaginatedProducts } from './types';
import { apiClient } from '@/utils/apiClient';

// The portal only lists products enabled for the web channel (case-sensitive).
export const MENU_CHANNEL = 'Web';

export const fetchProductMenu = createAsyncThunk<
  MenuItem[],
  { channel?: string },
  { rejectValue: string }
>(
  'products/fetchMenu',
  async ({ channel = MENU_CHANNEL }, { rejectWithValue }) => {
    try {
      const qs = channel ? `?channel=${encodeURIComponent(channel)}` : '';
      const resp = await apiClient(`/data/api/v1/products/menu/${qs}`, {
        method: 'GET',
      });
      return resp as MenuItem[];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to load product menu');
    }
  },
  {
    // Several components (desktop menu, mobile menu, services section) may
    // dispatch in the same tick; only let the first one hit the network.
    condition: (_, { getState }) => {
      const { products } = getState() as {
        products: { menu: { status: string } };
      };
      return products.menu.status !== 'loading';
    },
  }
);

export const fetchProducts = createAsyncThunk<
  PaginatedProducts,
  { name?: string; platforms?: string[] },
  { rejectValue: string }
>('products/fetchList', async ({ name, platforms }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    if (platforms) platforms.forEach((p) => params.append('platforms', p));
    const resp = await apiClient(`/data/api/v1/products/?${params}`, {
      method: 'GET',
    });
    return resp as PaginatedProducts;
  } catch (err: any) {
    return rejectWithValue(err.message || 'Failed to load products');
  }
});

export const fetchSubProductsLevel = createAsyncThunk<
  ProductListItem[],
  { level: 1 | 2 | 3 | 4 | 5 },
  { rejectValue: string }
>('products/fetchSubLevel', async ({ level }, { rejectWithValue }) => {
  try {
    const resp = await apiClient(`/data/api/v1/products/level${level}/`, {
      method: 'GET',
    });
    return resp as ProductListItem[];
  } catch (err: any) {
    return rejectWithValue(
      err.message || `Failed to load sub-products level ${level}`
    );
  }
});
